import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";

const SETTORI = ["Manifatturiero", "Commercio", "Servizi", "Edilizia", "ICT/Software", "Agroalimentare", "Altro"];
const TIPI = ["Azienda", "Professionista"];
const RUOLI = ["Titolare o Legale rappresentante", "Dipendente o Funzionario", "Consulente esterno"];

const validaPartitaIva = (piva) => {
  if (!/^\d{11}$/.test(piva)) return false;
  let total = 0;
  for (let i = 0; i < 10; i++) {
    let d = parseInt(piva[i], 10);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  const check = (10 - (total % 10)) % 10;
  return check === parseInt(piva[10], 10);
};

const Field = ({ label, children, help }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-[#2B3A42]">{label}</Label>
    {children}
    {help && <p className="text-xs text-[#5B6E78]">{help}</p>}
  </div>
);

export const CompanyForm = ({ value, onBack, onNext }) => {
  const [f, setF] = useState(value);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (!f.referente.trim()) return "Il nome e cognome del referente è obbligatorio.";
    if (!f.ragione_sociale.trim()) return "La ragione sociale è obbligatoria.";
    if (!validaPartitaIva(f.partita_iva.trim()))
      return "La partita IVA inserita non risulta corretta, verifica e riprova.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()))
      return "Inserisci un indirizzo email valido.";
    if (!f.settore) return "Seleziona il settore.";
    if (!f.tipo_utente) return "Seleziona il tipo utente.";
    if (!f.ruolo) return "Seleziona il ruolo.";
    return null;
  };

  const submit = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    onNext(f);
  };

  const inputCls = "h-11 bg-white border-[#DDE5E2] focus-visible:ring-[#6C8EAD]";

  return (
    <div className="animate-rise" data-testid="company-form">
      <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#2B3A42]">
        Dati azienda e referente
      </h2>
      <p className="mt-2 text-[#5B6E78] text-sm">Inserisci i dati richiesti per procedere con la simulazione.</p>

      <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Nome e cognome del referente *">
          <Input data-testid="input-contact-name" className={inputCls} value={f.referente}
            onChange={(e) => up("referente", e.target.value)} placeholder="Es. Mario Rossi" />
        </Field>
        <Field label="Ragione sociale *">
          <Input data-testid="input-company-name" className={inputCls} value={f.ragione_sociale}
            onChange={(e) => up("ragione_sociale", e.target.value)} placeholder="Es. Test Srl" />
        </Field>
        <Field label="Partita IVA *">
          <Input data-testid="input-vat-number" className={inputCls} value={f.partita_iva} inputMode="numeric"
            maxLength={11} onChange={(e) => up("partita_iva", e.target.value.replace(/\D/g, ""))}
            placeholder="11 cifre" />
        </Field>
        <Field label="Email del referente *">
          <Input data-testid="input-contact-email" className={inputCls} type="email" value={f.email}
            onChange={(e) => up("email", e.target.value)} placeholder="nome@azienda.it" />
        </Field>
        <Field label="Cellulare" help="Facoltativo. +39 e numero che inizia per 3.">
          <Input data-testid="input-contact-phone" className={inputCls} inputMode="tel" value={f.cellulare}
            onChange={(e) => up("cellulare", e.target.value)} placeholder="+39 333 1234567" />
        </Field>
        <Field label="Settore *">
          <Select value={f.settore} onValueChange={(v) => up("settore", v)}>
            <SelectTrigger data-testid="select-settore" className={inputCls}><SelectValue placeholder="Seleziona settore" /></SelectTrigger>
            <SelectContent>{SETTORI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Tipo utente *">
          <Select value={f.tipo_utente} onValueChange={(v) => up("tipo_utente", v)}>
            <SelectTrigger data-testid="select-tipo-utente" className={inputCls}><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>{TIPI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Ruolo *">
          <Select value={f.ruolo} onValueChange={(v) => up("ruolo", v)}>
            <SelectTrigger data-testid="select-ruolo" className={inputCls}><SelectValue placeholder="Seleziona ruolo" /></SelectTrigger>
            <SelectContent>{RUOLI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <div className="mt-6 space-y-3">
        <div className="flex items-center justify-between gap-4 rounded-xl border border-[#DDE5E2] bg-white px-4 py-3">
          <span className="text-sm text-[#2B3A42]">L'azienda ha già un revisore legale o un collegio sindacale?</span>
          <Switch data-testid="switch-revisore" checked={f.ha_revisore} onCheckedChange={(v) => up("ha_revisore", v)} />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-xl border border-[#DDE5E2] bg-white px-4 py-3">
          <span className="text-sm text-[#2B3A42]">L'azienda dispone di un DURC in corso di validità?</span>
          <Switch data-testid="switch-durc" checked={f.ha_durc} onCheckedChange={(v) => up("ha_durc", v)} />
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between gap-3">
        <Button variant="ghost" data-testid="company-back-btn" onClick={onBack}
          className="text-[#5B6E78] hover:text-[#2B3A42]">Indietro</Button>
        <Button data-testid="btn-step-company-next" onClick={submit}
          className="h-11 px-8 rounded-full bg-[#6C8EAD] hover:bg-[#4F708C] text-white">Continua</Button>
      </div>
    </div>
  );
};
